import { ApiResult } from "@/types/api";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  GetSpaAndMassageListingParams,
  getSpaFilterCriteriaAction,
  getSpaMsgCarouselAction,
  getSpaMsgDetailAction,
  handleGetSpaAndMassageListing,
  handleSearchSpaAction,
  SearchSpaParams,
  SpaFilterCriteriaResponse,
} from "@/actions/spa.action";
import {
  SpaData,
  SpaListingItem,
  SpaListingRes,
  SpaMsgDetail,
} from "@/components/main-services/spa-msg/spa-msg.type";

const spmQueryKeys = {
  carousel: (params?: { provinceId?: number; lat?: number; lng?: number }) =>
    ["spm-carousel", params] as const,
  detail: (spaId: string, params: { start: string }) =>
    ["spm-detail", spaId, params] as const,
  listing: (params: GetSpaAndMassageListingParams) =>
    ["spm-listings", params] as const,
  search: (params: SearchSpaParams) => ["spm-search", params] as const,
  criteria: () => ["spm-filter-criteria"] as const,
};

/** ******************************
 * Spa Listing Action Carousel
 ****************************** */
const useSpaAndMassageCarousel = (
  params?: { provinceId?: number; lat?: number; lng?: number },
  enabled = true,
) => {
  return useQuery<ApiResult<SpaData>, Error, SpaData>({
    queryKey: spmQueryKeys.carousel(params),
    queryFn: () => getSpaMsgCarouselAction(params),
    enabled,
    staleTime: 1000 * 60 * 5,
    select: (res) => res.data,
  });
};

/** ******************************
 * Spa Detail Action
 ****************************** */
const useSpaAndMassageDetail = (spaId: string, params: { start: string }) => {
  return useQuery<ApiResult<SpaMsgDetail>, Error, SpaMsgDetail>({
    queryKey: spmQueryKeys.detail(spaId, params),
    queryFn: () => getSpaMsgDetailAction(spaId, params),
    enabled: Boolean(spaId),
    staleTime: 5 * 60 * 1000,
    select: (res) => res.data, // unwraps ApiResult<SpaMsgDetail> -> SpaMsgDetail
  });
};

/** ******************************
 * Spa Listing Action
 ****************************** */
const useGetSpaAndMassageListing = (
  params: GetSpaAndMassageListingParams,
  enabled = true,
): UseQueryResult<SpaListingItem[], Error> => {
  return useQuery({
    queryKey: spmQueryKeys.listing(params),
    queryFn: () => handleGetSpaAndMassageListing(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    select: (res: ApiResult<SpaListingRes>) => res.data.data,
  });
};

/** ******************************
 * Spa Search Hook
 ****************************** */
const useSpaAndMassageSearch = (
  params: SearchSpaParams,
  enabled = true,
): UseQueryResult<SpaListingItem[], Error> => {
  return useQuery({
    queryKey: spmQueryKeys.search(params),
    queryFn: () => handleSearchSpaAction(params),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: true,
    enabled,
    placeholderData: keepPreviousData,
    select: (res: ApiResult<SpaListingRes>) => {
      if (!res?.data) return [];
      if (Array.isArray(res.data)) return res.data;
      return res.data.data ?? [];
    },
  });
};

/** ******************************
 * Spa Filter Criteria Hook
 ****************************** */
const useSearchSpaMutation = () => {
  return useMutation<ApiResult<SpaListingRes>, Error, SearchSpaParams>({
    mutationFn: (params) => handleSearchSpaAction(params),
  });
};

const useSpaFilterCriteria = (enabled = true) => {
  return useQuery<SpaFilterCriteriaResponse>({
    queryKey: spmQueryKeys.criteria(),
    queryFn: () => getSpaFilterCriteriaAction(),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
};

export {
  spmQueryKeys,
  useSpaAndMassageCarousel,
  useSpaAndMassageDetail,
  useGetSpaAndMassageListing,
  useSpaAndMassageSearch,
  useSearchSpaMutation,
  useSpaFilterCriteria,
};
